import React, { FunctionComponent, useCallback, useState } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import CTAButton, { ctaEnums } from '../../../@shared/ctaButton';
import CustomExternalAnchor from '../../../@shared/customExternalAnchor';
import { CustomText } from '../../../@shared/customText';
import Picture from '../../../@shared/picture';
import RoutableSitecoreLink from '../../../@shared/routableSitecoreLink';
import { SpecialOffersSlideModel } from '../../models/cardCarousel.model';
import { targetLinkdsk } from '../../../../utils/targetDskLink';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../../.././constants/breakpoints.constants';
import { googleClickCtaEvent } from '../../../../utils/analytics';
import { StringField } from '../../../../types/types';
import Icon from '../../../@shared/icon';
import ModalDialog from '../../../@shared/modalDialog';
import OverlayCarouselPopup from '../../../@shared/overlayCarouselPopup';
import { getImageField } from '../../../../utils/getImageFields';
import { getThumbnailProps } from '../../../../utils/youtubeUtils';
import { iconSize } from '../../../../constants/icon.constants';
import { getQueryLink } from '../../../../utils/getQuerylink';

const CarouselSlide: FunctionComponent<{
  slideIndex: number;
  model: SpecialOffersSlideModel;
  fallbackAnalyticsTitle: string | undefined;
  isEE: boolean;
  cardHeadingTag;
}> = ({ slideIndex, model, fallbackAnalyticsTitle, isEE, cardHeadingTag }) => {
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const [showOverlay, setShowOverlay] = useState(false);

  const { thumbnailImgSrc } = getThumbnailProps(model.video?.value?.href || '');
  const imageOrThumbnail = getImageField(model.image, thumbnailImgSrc || '', model.useVideo);
  const mobileImageOrThumbnail = getImageField(model.mobileImage, thumbnailImgSrc || '', model.useVideo);
  
  const closeOverlayModal = (): void => setShowOverlay(false);
  model?.buttonPrimary && getQueryLink(model?.buttonPrimary)
  model?.buttonSecondary && getQueryLink(model?.buttonSecondary)

  const showOverlayVideo = (): void => {
    if (!model.useVideo) return;
    setShowOverlay(true);
  };

  const getOverlayProps = useCallback(() => {
    return [
      {
        desktopImage: imageOrThumbnail,
        mobileImage: mobileImageOrThumbnail,
        fullImage: imageOrThumbnail,
        desktopAlt: model.image?.value?.alt as string,
        mobileAlt: model.mobileImage?.value?.alt as string,
        isVideo: model.useVideo,
        videoUrl: model.video?.value?.href,
      },
    ];
  }, [model, thumbnailImgSrc]);

  return (
    <>
      {model?.badgeLink?.value?.href && model?.badgeLink?.value?.text && (
        <div className="card-label card-label--clickable">
          <CustomExternalAnchor field={model?.badgeLink} target={targetLinkdsk(model?.badgeLink, isDesktop)} />
        </div>
      )}
      <div className={`special-offers-card special-offers-card--${model?.isWideCard ? 'large' : 'small'}`}>
        <div className="special-offers-card-wrapper slide-card slide-card--regular special-offer-homepage-card special-offers-card-wrapper">
          <div className="special-offer-homepage-card__image-wrapper">
            <Picture
              editorialImageProps={{
                id: model.id,
                desktopImage: model?.image,
                mobileImage: model?.mobileImage,
                desktopTransformation: model?.isWideCard ? 'Size1000' : 'Size500',
                mobileTransformation: 'Size300',
                cssClassPictureEl: 'slide-card__image',
              }}
            />
            {model.useVideo && (
              <div onClick={showOverlayVideo} className="special-offer-homepage-card__video-play-button">
                <Icon extraClassName='special-offer-homepage-card__video-play-button--icon' iconName="action-play" size={iconSize.xlg} color="primary" />
              </div>
            )}
          </div>
          <div className={'special-offer-homepage-body'}>
            <div className="special-offers-card__text-wrapper">
              <CustomText tag={cardHeadingTag} field={model?.title as StringField} className="special-offer-homepage-body__title" />
              <RichText
                tag="div"
                field={model?.subtitle as StringField}
                className="special-offer-homepage-body__subtitle rich-text color-primary-link"
              />
            </div>
            <div className={'special-offer-homepage-body__cta-container'}>
              {(isEE || (model?.buttonPrimary?.value?.text && model?.buttonPrimary?.value?.href)) && (
                <CTAButton type={ctaEnums.primaryRed}>
                  <RoutableSitecoreLink
                    className="slide-card__button slide-card__button--primary"
                    field={model?.buttonPrimary}
                    target={targetLinkdsk(model?.buttonPrimary, isDesktop)}
                    onClick={() =>
                      googleClickCtaEvent(
                        'Button',
                        model?.buttonPrimary?.value?.text,
                        model.title?.value || fallbackAnalyticsTitle,
                        slideIndex + 1
                      )
                    }
                  />
                </CTAButton>
              )}
              {((model?.type?.value !== 'offerCardWide' && !(model?.buttonPrimary?.value?.text && model?.buttonPrimary?.value?.href)) ||
                model?.type?.value === 'offerCardWide') &&
                (isEE || (model?.buttonSecondary?.value?.text && model?.buttonSecondary?.value?.href)) && (
                  <CTAButton type={ctaEnums.primaryWhite}>
                    <RoutableSitecoreLink
                      className="slide-card__button slide-card__button--secondary"
                      field={model?.buttonSecondary}
                      target={targetLinkdsk(model?.buttonSecondary, isDesktop)}
                      onClick={() =>
                        googleClickCtaEvent(
                          'Button',
                          model?.buttonSecondary?.value?.text,
                          model.title?.value || fallbackAnalyticsTitle,
                          slideIndex + 1
                        )
                      }
                    />
                  </CTAButton>
                )}
            </div>
          </div>
        </div>
      </div>
      {model.video && model.useVideo && (
        <ModalDialog isOpen={showOverlay} close={closeOverlayModal}>
          <OverlayCarouselPopup slides={getOverlayProps()} editorial={true}/>
        </ModalDialog>
      )}
    </>
  );
};

export default CarouselSlide;
